<template>
  <div style="padding: 16px; background: #F8F8F9; display: flex; flex-direction: column; gap: 12px;">
    <!-- Header panel -->
    <div class="cm-panel">
      <div class="cm-panel-head">
        <div class="cm-panel-title">ADT Pipeline</div>
        <div class="cm-panel-head-actions">
          <div class="cm-stepper">
            <button class="cm-icon-btn" @click="shiftDay(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
            <input class="cm-input cm-input-sm" type="date" v-model="selectedDate" @change="fetchMessages" style="width: 108px; text-align: center;" />
            <button class="cm-icon-btn" @click="shiftDay(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
          </div>
          <button class="cm-btn cm-btn-sm" :class="isToday ? 'cm-btn-primary' : 'cm-btn-secondary'" @click="goToday">Today</button>
          <button class="cm-icon-btn" @click="fetchMessages" title="Refresh"><FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" /></button>
        </div>
      </div>
      <div v-if="!loading && messages.length" style="padding: 10px 16px; font-size: 11.5px; color: #8B8B8D;">
        <strong style="color: #626264;">{{ messages.length.toLocaleString() }}</strong> messages ·
        <strong style="color: #626264;">{{ groupedRows.length.toLocaleString() }}</strong> episodes/events
        <span v-if="hasActiveFilters" style="margin-left: 8px; color: #B45309;">
          (filtered)
          <button @click="clearFilters" style="text-decoration: underline; background: none; border: none; color: inherit; cursor: pointer; font: inherit;">clear</button>
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <!-- Table -->
    <div v-else-if="groupedRows.length" class="cm-panel" style="max-height: 600px; overflow-y: auto;">
      <div style="overflow-x: auto;">
        <table class="cm-table" style="min-width: 1260px; table-layout: fixed;">
          <thead>
            <tr>
              <th style="width: 28px;"></th>
              <th class="sortable" style="width: 82px;" @click="setSort('mbi')">MBI<span v-if="sortKey==='mbi'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 100px;" @click="setSort('lastName')">Last<span v-if="sortKey==='lastName'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 80px;" @click="setSort('firstName')">First<span v-if="sortKey==='firstName'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 120px;" @click="setSort('episodeType')">Episode<span v-if="sortKey==='episodeType'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 90px;" @click="setSort('disposition')">Disposition<span v-if="sortKey==='disposition'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 140px;" @click="setSort('facility')">Facility<span v-if="sortKey==='facility'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 110px;" @click="setSort('start')">Start<span v-if="sortKey==='start'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable num" style="width: 50px;" @click="setSort('los')">LOS<span v-if="sortKey==='los'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 140px;" @click="setSort('practiceName')">Practice<span v-if="sortKey==='practiceName'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 90px;" @click="setSort('cmTags')">CM Tags<span v-if="sortKey==='cmTags'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 55px;" @click="setSort('laceInfo')">LACE<span v-if="sortKey==='laceInfo'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable" style="width: 55px;" @click="setSort('echoInfo')">ECHO<span v-if="sortKey==='echoInfo'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable ctr" style="width: 80px;" @click="setSort('activeOpps')">Active Opps<span v-if="sortKey==='activeOpps'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
              <th class="sortable num" style="width: 55px;" @click="setSort('eventCount')">Events<span v-if="sortKey==='eventCount'" style="color: #F76734; margin-left: 3px;">{{ sortAsc ? '↑' : '↓' }}</span></th>
            </tr>
            <!-- Filter row -->
            <tr style="background: #F8F8F9; border-bottom: 1px solid #E8E9E9;">
              <th></th>
              <th style="padding: 4px 6px;"><input v-model="filters.mbi" data-demo-search placeholder="Filter…" class="sensitive cm-input cm-input-sm" style="width: 100%;" /></th>
              <th style="padding: 4px 6px;"><input v-model="filters.lastName" data-demo-search placeholder="Filter…" class="sensitive cm-input cm-input-sm" style="width: 100%;" /></th>
              <th style="padding: 4px 6px;"><input v-model="filters.firstName" data-demo-search placeholder="Filter…" class="sensitive cm-input cm-input-sm" style="width: 100%;" /></th>
              <th style="padding: 4px 6px;"><select v-model="filters.episodeType" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option v-for="v in filterOptions.episodeType" :key="v" :value="v">{{ v }}</option></select></th>
              <th style="padding: 4px 6px;"><select v-model="filters.disposition" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option v-for="v in filterOptions.disposition" :key="v" :value="v">{{ v }}</option></select></th>
              <th style="padding: 4px 6px;"><input v-model="filters.facility" data-demo-search placeholder="Filter…" class="sensitive cm-input cm-input-sm" style="width: 100%;" /></th>
              <th></th>
              <th></th>
              <th style="padding: 4px 6px;"><input v-model="filters.practiceName" data-demo-search placeholder="Filter…" class="sensitive cm-input cm-input-sm" style="width: 100%;" /></th>
              <th style="padding: 4px 6px;"><select v-model="filters.cmTags" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option v-for="v in filterOptions.cmTags" :key="v" :value="v">{{ v }}</option></select></th>
              <th style="padding: 4px 6px;"><select v-model="filters.laceInfo" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option value="__has__">Has</option><option value="__none__">None</option></select></th>
              <th style="padding: 4px 6px;"><select v-model="filters.echoInfo" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option value="__has__">Has</option><option value="__none__">None</option></select></th>
              <th style="padding: 4px 6px;"><select v-model="filters.activeOpps" class="cm-select cm-select-sm" style="width: 100%;"><option value="">All</option><option value="__has__">Has opps</option><option value="__none__">Never created</option><option value="__closed__">Worked &amp; closed</option></select></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in groupedRows" :key="row.key">
              <tr :style="{ cursor: row.isEpisode ? 'pointer' : 'default' }" @click="row.isEpisode && toggleExpand(row.key)">
                <td style="text-align: center; color: #A5A5A6;">
                  <FontAwesomeIcon v-if="row.isEpisode" :icon="['fas', expanded[row.key] ? 'chevron-down' : 'chevron-right']" style="font-size: 9px;" />
                </td>
                <td class="sensitive" data-spoof-kind="identifier" style="font-family: ui-monospace, monospace;"><span class="strong">{{ row.mbi }}</span></td>
                <td class="sensitive" data-spoof-kind="person-last"><span class="strong">{{ row.lastName || '—' }}</span></td>
                <td class="sensitive" data-spoof-kind="person-first">{{ row.firstName || '—' }}</td>
                <td>
                  <span v-if="row.episodeType" class="cm-badge" :class="epTone(row.episodeType)">{{ epLabel(row.episodeType) }}</span>
                  <span v-else style="color: #A5A5A6; font-style: italic; font-size: 10px;">{{ row.event_type || 'no episode' }}</span>
                </td>
                <td>{{ row.disposition || '—' }}</td>
                <td class="sensitive" data-spoof-kind="facility" style="max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="row.facility">{{ row.facility || '—' }}</td>
                <td>{{ formatDt(row.start) }}</td>
                <td class="num">{{ row.los ?? '—' }}</td>
                <!-- The practice name is tagged on its own span so the caveat
                     marker and resolution label are not spoofed as practices.
                     The cell-level tooltip ("Name — label") is the real name,
                     so it is bound only with Demo Mode off; in Demo Mode the
                     tagged span carries the (spoofed) name tooltip instead. -->
                <td style="max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="practiceCellTitle(row)">
                  <span v-if="row.practiceName"><span class="sensitive" data-spoof-kind="practice" :title="demoMasking ? row.practiceName : null">{{ row.practiceName }}</span><span v-if="practiceIsCaveated(row.practiceResolutionStatus)" style="color: #8B8B8D;" :title="demoMasking ? practiceResolutionLabel(row.practiceResolutionStatus) : null">&nbsp;*</span></span>
                  <span v-else style="color: #8B8B8D; font-style: italic;">{{ practiceResolutionLabel(row.practiceResolutionStatus) }}</span>
                </td>
                <td>
                  <span v-if="row.cmTags" style="display: flex; gap: 3px; flex-wrap: wrap;">
                    <span v-for="tag in parseTags(row.cmTags)" :key="tag.full" class="cm-badge" :class="tag.tone" :title="tag.full">{{ tag.abbr }}</span>
                  </span>
                  <span v-else style="color: #D7D7D8;">—</span>
                </td>
                <td :title="row.laceInfo || scoreResolutionLabel(row.laceResolutionStatus)">
                  <span v-if="row.laceInfo" class="strong">{{ scoreOnly(row.laceInfo) }}</span>
                  <span v-else style="color: #D7D7D8;">—</span>
                </td>
                <td :title="row.echoInfo || scoreResolutionLabel(row.echoResolutionStatus)">
                  <span v-if="row.echoInfo" class="strong">{{ scoreOnly(row.echoInfo) }}</span>
                  <span v-else style="color: #D7D7D8;">—</span>
                </td>
                <td class="ctr">
                  <template v-if="row.activeOpps">
                    <span
                      v-for="prog in oppList(row.activeOpps)"
                      :key="prog"
                      class="cm-badge"
                      :class="oppBadgeClass(prog)"
                      :title="prog"
                      style="margin: 1px;"
                    >{{ oppLabel(prog) }}</span>
                  </template>
                  <!-- No ACTIVE program, but this episode's own case was worked
                       and closed. A dash here reads as "nothing was ever
                       created", which is a different and much worse fact.
                       Rendered as the same badge shape as an active program,
                       greyed, with the dates on hover — this column is scanned,
                       not read. -->
                  <span
                    v-else-if="row.episodeOppStatus"
                    class="cm-badge"
                    :title="episodeOppTitle(row)"
                    style="margin: 1px; opacity: 0.55;"
                  >{{ oppLabel(row.episodeOppProgram) }}</span>
                  <span v-else style="color: #D7D7D8;">—</span>
                </td>
                <td class="num">{{ row.eventCount }}</td>
              </tr>
              <!-- Expanded child events -->
              <template v-if="row.isEpisode && expanded[row.key]">
                <tr v-for="evt in row.events" :key="evt.message_id" style="background: #F8F8F9;">
                  <td style="text-align: right; color: #D2D2D4;">↳</td>
                  <td class="sensitive" data-spoof-kind="identifier" style="font-family: ui-monospace, monospace; color: #A5A5A6; font-size: 10px;">{{ evt.mbi }}</td>
                  <td colspan="2" style="color: #8B8B8D;">
                    <span style="font-family: ui-monospace, monospace;">{{ evt.trigger_event }}</span>
                    <span style="color: #A5A5A6; margin-left: 4px;">{{ evt.event_type }}</span>
                  </td>
                  <td></td>
                  <td style="color: #8B8B8D;">{{ evt.visit_discharge_disposition || '—' }}</td>
                  <td class="sensitive" data-spoof-kind="facility" style="color: #8B8B8D;" :title="evt.location_facility">{{ evt.location_facility || '—' }}</td>
                  <td colspan="2" style="color: #A5A5A6;">{{ formatDt(evt.message_datetime) }}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style="font-family: ui-monospace, monospace; color: #A5A5A6; text-align: right;">{{ evt.patient_class_code }}</td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="cm-empty">
      <FontAwesomeIcon :icon="['fas', 'table']" class="ico" />
      <div class="msg">No ADT data for this date</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import demoProtectedFields from '@/mixins/demoProtectedFields';
import { isDemoMode } from '@/utils/demoSpoof';

const TAG_TONES = {
  'Vytal Care - TOC': { abbr: 'TOC', tone: 'bad' },
  'Vytal Care- TOC ED': { abbr: 'ED', tone: 'warn' },
  'Vytal Care- TOC ED Low': { abbr: 'EDL', tone: 'warn' },
  'Vytal Care- TOC Low LACE': { abbr: 'Low', tone: 'bad' },
  'Vytal Care TOC- Private Payer': { abbr: 'PP', tone: '' },
  'Vytal Care+': { abbr: 'VC+', tone: 'info' },
  'Vytal Care+ TOC': { abbr: 'VC+T', tone: 'info' },
  'CDM': { abbr: 'CDM', tone: 'info' },
  'SNF': { abbr: 'SNF', tone: 'warn' },
  'LCSW': { abbr: 'LCSW', tone: '' },
  'Direct Referral': { abbr: 'DR', tone: '' },
};

const EP_LABELS = {
  INPATIENT: 'Inpatient',
  ED_VISIT: 'ED Visit',
  OBSERVATION_STAY: 'Observation',
  OUTPATIENT_PROCEDURE: 'Outpatient',
  OTHER: 'Other',
};

export default {
  name: 'CareManagement_Pipeline',
  components: { FontAwesomeIcon },
  mixins: [demoProtectedFields],
  // Free-text filters over masked columns hold real patient, facility and
  // practice terms. Demo Mode empties them and the tagged inputs are read-only;
  // the category selects (episode, disposition, tags, LACE/ECHO, opps) stay
  // usable. The filters live only in memory: nothing here reads storage or the
  // route, so a remount starts empty.
  demoProtectedFields: ['filters.mbi', 'filters.lastName', 'filters.firstName', 'filters.facility', 'filters.practiceName'],
  data() {
    const toLocal = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return {
      loading: false,
      selectedDate: toLocal(new Date()),
      messages: [],
      totalCount: 0,
      expanded: {},
      sortKey: 'episodeType',
      sortAsc: true,
      filters: {
        mbi: '', lastName: '', firstName: '', episodeType: '', disposition: '',
        facility: '', practiceName: '', cmTags: '', eligibleSlug: '',
        laceInfo: '', echoInfo: '', cmOpp: '', activeOpps: '',
      },
    };
  },
  computed: {
    demoMasking() {
      return isDemoMode();
    },
    isToday() {
      const d = new Date();
      return this.selectedDate === `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },
    hasActiveFilters() { return Object.values(this.filters).some(v => v !== ''); },
    filterOptions() {
      const episodes = {};
      const standalones = [];
      for (const msg of this.messages) {
        if (msg.episode_id != null) {
          const key = msg.mbi + '|' + msg.episode_id;
          if (!episodes[key]) episodes[key] = { episodeType: msg.episode_type, disposition: msg.disposition_category, cmTags: msg.cm_tags, eligibleSlug: msg.eligible_slug };
        } else {
          standalones.push({ episodeType: null, disposition: msg.visit_discharge_disposition, cmTags: msg.cm_tags, eligibleSlug: msg.eligible_slug });
        }
      }
      const all = [...Object.values(episodes), ...standalones];
      const unique = (key) => [...new Set(all.map(r => r[key]).filter(Boolean))].sort();
      const tagSet = new Set();
      all.forEach(r => { if (r.cmTags) r.cmTags.split(', ').forEach(t => tagSet.add(t.trim())); });
      return { episodeType: unique('episodeType'), disposition: unique('disposition'), eligibleSlug: unique('eligibleSlug'), cmTags: [...tagSet].sort() };
    },
    groupedRows() {
      const episodes = {};
      const standalones = [];
      for (const msg of this.messages) {
        if (msg.episode_id != null) {
          const key = msg.mbi + '|' + msg.episode_id;
          if (!episodes[key]) {
            episodes[key] = {
              key, isEpisode: true, mbi: msg.mbi, lastName: msg.patient_name_last, firstName: msg.patient_name_first,
              dob: msg.date_of_birth, episodeType: msg.episode_type, disposition: msg.disposition_category,
              facility: msg.episode_facility || msg.location_facility, start: msg.episode_start, end: msg.episode_end,
              los: msg.episode_duration_hours, practiceName: msg.practice_name,
              practiceResolutionStatus: msg.practice_resolution_status, cmTags: msg.cm_tags,
              eligibleSlug: msg.eligible_slug, laceInfo: msg.lace_info, echoInfo: msg.echo_info,
              laceResolutionStatus: msg.lace_resolution_status, echoResolutionStatus: msg.echo_resolution_status,
              cmOpp: msg.opp_program, activeOpps: msg.active_cm_programs,
              episodeOppProgram: msg.episode_opp_program, episodeOppStatus: msg.episode_opp_status,
              episodeOppCreatedAt: msg.episode_opp_created_at, episodeOppCompletedAt: msg.episode_opp_completed_at,
              events: [],
            };
          }
          episodes[key].events.push(msg);
        } else {
          standalones.push({
            key: msg.message_id, isEpisode: false, mbi: msg.mbi, lastName: msg.patient_name_last,
            firstName: msg.patient_name_first, dob: msg.date_of_birth, episodeType: null, event_type: msg.event_type,
            disposition: msg.visit_discharge_disposition, facility: msg.location_facility, start: msg.message_datetime,
            end: null, los: null, practiceName: msg.practice_name,
            practiceResolutionStatus: msg.practice_resolution_status, cmTags: msg.cm_tags, eligibleSlug: msg.eligible_slug,
            laceInfo: msg.lace_info, echoInfo: msg.echo_info,
            laceResolutionStatus: msg.lace_resolution_status, echoResolutionStatus: msg.echo_resolution_status,
            cmOpp: msg.opp_program, activeOpps: msg.active_cm_programs,
            episodeOppProgram: msg.episode_opp_program, episodeOppStatus: msg.episode_opp_status,
            episodeOppCreatedAt: msg.episode_opp_created_at, episodeOppCompletedAt: msg.episode_opp_completed_at,
            eventCount: 1, events: [msg],
          });
        }
      }
      let all = [...Object.values(episodes).map(ep => ({ ...ep, eventCount: ep.events.length })), ...standalones];
      const f = this.filters;
      if (f.mbi) all = all.filter(r => (r.mbi || '').toLowerCase().includes(f.mbi.toLowerCase()));
      if (f.lastName) all = all.filter(r => (r.lastName || '').toLowerCase().includes(f.lastName.toLowerCase()));
      if (f.firstName) all = all.filter(r => (r.firstName || '').toLowerCase().includes(f.firstName.toLowerCase()));
      if (f.episodeType) all = all.filter(r => r.episodeType === f.episodeType);
      if (f.disposition) all = all.filter(r => r.disposition === f.disposition);
      if (f.facility) all = all.filter(r => (r.facility || '').toLowerCase().includes(f.facility.toLowerCase()));
      if (f.practiceName) all = all.filter(r => (r.practiceName || '').toLowerCase().includes(f.practiceName.toLowerCase()));
      if (f.cmTags) all = all.filter(r => (r.cmTags || '').includes(f.cmTags));
      if (f.eligibleSlug) all = all.filter(r => r.eligibleSlug === f.eligibleSlug);
      if (f.laceInfo === '__has__') all = all.filter(r => r.laceInfo);
      if (f.laceInfo === '__none__') all = all.filter(r => !r.laceInfo);
      if (f.echoInfo === '__has__') all = all.filter(r => r.echoInfo);
      if (f.echoInfo === '__none__') all = all.filter(r => !r.echoInfo);
      if (f.cmOpp === '__has__') all = all.filter(r => r.cmOpp);
      if (f.cmOpp === '__none__') all = all.filter(r => !r.cmOpp);
      if (f.activeOpps === '__has__') all = all.filter(r => r.activeOpps);
      // "No opps" must mean NOTHING WAS EVER CREATED for this episode. An
      // episode whose case was worked and closed is not a creation failure, and
      // including it here is what made the filter look full of missing opps.
      if (f.activeOpps === '__none__') all = all.filter(r => !r.activeOpps && !r.episodeOppStatus);
      if (f.activeOpps === '__closed__') all = all.filter(r => !r.activeOpps && r.episodeOppStatus);
      const key = this.sortKey, dir = this.sortAsc ? 1 : -1;
      all.sort((a, b) => { const av = a[key] ?? '', bv = b[key] ?? ''; return (av < bv ? -1 : av > bv ? 1 : 0) * dir; });
      return all;
    },
  },
  methods: {
    // Cell tooltip: the practice name with its resolution caveat, or the
    // resolution label when there is no practice. With Demo Mode on the name
    // would bypass the tag, so only the (non-identifying) label is kept.
    practiceCellTitle(row) {
      const label = this.practiceResolutionLabel(row.practiceResolutionStatus);
      if (!row.practiceName) return label;
      if (this.demoMasking) return null;
      return this.practiceIsCaveated(row.practiceResolutionStatus) ? row.practiceName + ' — ' + label : row.practiceName;
    },
    clearFilters() { Object.keys(this.filters).forEach(k => { this.filters[k] = ''; }); },
    formatDt(d) {
      if (!d) return '—';
      const dt = new Date(d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
             dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    },
    setSort(key) { if (this.sortKey === key) this.sortAsc = !this.sortAsc; else { this.sortKey = key; this.sortAsc = true; } },
    parseTags(tagStr) {
      if (!tagStr) return [];
      return tagStr.split(', ').map(t => {
        const m = TAG_TONES[t.trim()];
        return m ? { full: t.trim(), ...m } : { full: t.trim(), abbr: t.trim().slice(0, 4), tone: '' };
      });
    },
    episodeOppTitle(row) {
      if (!row.episodeOppStatus) return '';
      const created = row.episodeOppCreatedAt ? this.formatDt(row.episodeOppCreatedAt) : '?';
      const done = row.episodeOppCompletedAt ? this.formatDt(row.episodeOppCompletedAt) : null;
      const base = `${row.episodeOppProgram} (${row.episodeOppStatus.toLowerCase()}) for this episode — created ${created}`;
      return done ? `${base}, closed ${done}` : base;
    },
    practiceResolutionLabel(status) {
      return {
        identity_not_resolved: 'Identity not resolved',
        no_tin_assignment_for_episode: 'No TIN assignment for episode',
        tin_not_mapped_to_practice: 'TIN not mapped to practice',
        ambiguous_assignment_resolved_by_tiebreak: 'Resolved from multiple assignments',
        resolved: 'Resolved',
      }[status] || 'Practice unavailable';
    },
    // A practice CAN be resolved and still carry a caveat: EMPI returned more
    // than one current primary TIN and we picked one deterministically.
    practiceIsCaveated(status) {
      return status === 'ambiguous_assignment_resolved_by_tiebreak';
    },
    scoreResolutionLabel(status) {
      return {
        calculated: 'Calculated',
        component_not_received: 'Score unavailable — component not received; conservative route applies',
        component_matched_via_empi_refresh_pending: 'Component matched through EMPI — score refresh pending',
        identity_not_resolved: 'Score unavailable — patient identity not resolved; conservative route applies',
        flag_refresh_pending: 'Component received — score refresh pending',
        score_not_calculated: 'Component received, but the score could not be calculated',
        not_applicable: 'Not applicable to this episode type',
      }[status] || 'Score unavailable';
    },
    epTone(t) {
      return { INPATIENT: 'bad', ED_VISIT: 'warn', OBSERVATION_STAY: 'info', OUTPATIENT_PROCEDURE: '', OTHER: '' }[t] || '';
    },
    scoreOnly(v) {
      // "9.0 (2026-06-10)" → "9"
      if (!v) return '';
      const n = parseFloat(v);
      return isNaN(n) ? v : String(Math.round(n));
    },
    // Active Opps: comma-joined program slugs → one colored badge each.
    oppList(v) {
      return (v || '').split(',').map(s => s.trim()).filter(Boolean);
    },
    oppBadgeClass(prog) {
      // Distinct color per program so the column is scannable at a glance.
      const map = {
        IHCI_TOC_IP: 'info',        // inpatient TOC — blue
        IHCI_TOC_ED: 'warn',        // ED TOC — amber
        IHCI_SNF_ADMISSION: 'brand',// SNF — brand orange
        VYTALTOC: 'info', VYTALTOC_ED: 'warn', VYTALTOC_LOW_LACE: 'info',
        VYTALTOC_ED_LOW: 'warn', VYTALTOC_NO_ADMIT: 'info',
        SNF: 'brand', CDM: 'ok', LCSW: 'ok',
      };
      return map[prog] || 'ok';
    },
    oppLabel(prog) {
      // Shorten the slug for the badge; full slug stays in the title tooltip.
      const map = {
        IHCI_TOC_IP: 'TOC IP', IHCI_TOC_ED: 'TOC ED', IHCI_SNF_ADMISSION: 'SNF',
        VYTALTOC: 'TOC', VYTALTOC_ED: 'TOC ED', VYTALTOC_LOW_LACE: 'TOC Low',
        VYTALTOC_ED_LOW: 'ED Low', VYTALTOC_NO_ADMIT: 'TOC NA',
      };
      return map[prog] || prog.replace(/^IHCI_/, '').replace(/_/g, ' ');
    },
    epLabel(t) {
      return EP_LABELS[t] || t?.replace(/_/g, ' ').toLowerCase() || '—';
    },
    toggleExpand(key) { this.expanded = { ...this.expanded, [key]: !this.expanded[key] }; },
    _toLocal(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; },
    shiftDay(dir) { const d = new Date(this.selectedDate + 'T12:00:00'); d.setDate(d.getDate() + dir); this.selectedDate = this._toLocal(d); this.fetchMessages(); },
    goToday() { this.selectedDate = this._toLocal(new Date()); this.fetchMessages(); },
    async fetchMessages() {
      this.loading = true; this.messages = []; this.totalCount = 0; this.expanded = {};
      const nextDay = new Date(this.selectedDate + 'T12:00:00'); nextDay.setDate(nextDay.getDate() + 1);
      try {
        const { data } = await axios.post('/api/care-management/pipeline-funnel/messages/', { date_from: this.selectedDate, date_to: this._toLocal(nextDay) });
        this.messages = data.messages; this.totalCount = data.count;
      } catch (e) { console.error('Pipeline messages error:', e); }
      finally { this.loading = false; }
    },
  },
  mounted() { this.fetchMessages(); },
};
</script>
